import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import { Route, Switch } from "react-router-dom";
import { Theme, Spinner } from "discover.medical.react-component-library";
import { ConfigProvider } from "@eis-poc/lib-discover-medical-mfes";
import Epic from "./utils/epic";
import { RouteContent } from "./components/RouteContent/RouteContent.styled";
import NotFound from "./components/NotFound";
import SystemError from "./components/SystemError";
import AuthError from "./components/AuthError";
import Header from "./components/Header";
import AppFooter from "./components/AppFooter";
import EventBusJailService from "./components/EventBusJailService";
import ToolbarFooter from "./components/ToolbarFooter";
import MfeRoute from "./components/MfeRoute";
import CallToActionWrapper from "./components/CallToActionWrapper";

import { getThemeFriendlyProductCode } from "./utils/productCodeUtils";
import { StyledApp } from "./App.styled";
import { instantiateAmplitude } from "./utils/amplitudeHelper";
import {
  useOsano,
  isOsanoAnalyticsAllowed,
  hasOsano
} from "./utils/osanoHelper";
import { StoreProvider } from "./store";
import ErrorBoundary from "./components/ErrorBoundary";
import EventBus from "js-event-bus";
import GainsightService from "./components/GainsightService";

const SystemCheckWrapper = loadable(
  () => import("./components/SystemCheckWrapper"),
  {
    fallback: <Spinner size="lg" />
  }
);

const Assistant = loadable(
  () => import("./components/Assistant/NewAssistantProvider")
);

const App = ({ configData }) => {
  const feedbackUrl = configData?.feedbackUrl;
  const productCode = getThemeFriendlyProductCode(configData);
  const interfaceId = configData?.productInfo?.interfaceId;
  const {
    puaId,
    affiliationId,
    groupId,
    profileId,
    email,
    fullDisplayName,
    trafficSourceInfo: trafficSource,
    affiliationName,
    specialty,
    role,
    isAdminUser,
    isAnalyticsUser,
    isDynaAIUser,
    customFieldOne,
    customFieldTwo,
    customFieldThree,
    customFieldFour,
    customFieldFive
  } = configData?.header?.userDetails || {};
  const isOsanoEnabled = configData?.isOsanoEnabled;
  const applicationCode = configData?.productInfo?.applicationCode;
  const gainsightUserData = {
    puaId,
    fullDisplayName,
    email,
    applicationCode,
    affiliationId,
    affiliationName,
    specialty,
    role,
    trafficSource,
    isAdminUser,
    isAnalyticsUser,
    isDynaAIUser,
    customFieldOne,
    customFieldTwo,
    customFieldThree,
    customFieldFour,
    customFieldFive
  };

  //TODO: this config needs to be moved off footer
  //once new assistant is deployed
  //assistant.assistantButton will no longer be needed
  const assistant = configData.toolbarFooter?.assistant;
  const eventBus = useRef(new EventBus());

  useEffect(() => {
    const { amplitudeApiKey, analyticsApi } = configData;
    const isAnalyticsAllowed = isOsanoAnalyticsAllowed(isOsanoEnabled);
    const amplitudeInfo = { amplitudeApiKey, analyticsApi };
    const userData = {
      customerInfo: { customerId: affiliationId, groupId, profileId },
      puaId,
      trafficSource,
      interfaceId
    };

    instantiateAmplitude({
      amplitudeInfo,
      userData: isAnalyticsAllowed ? userData : null,
      optOut: !isAnalyticsAllowed
    });

    const epic = new Epic();
    epic.initiateHandshake();
  }, []);

  const { showOsanoDrawer } = useOsano({
    customerId: affiliationId,
    groupId,
    profileId,
    puaId,
    interfaceId,
    trafficSource
  });

  return (
    <ErrorBoundary>
      <StoreProvider>
        <Theme ui={productCode}>
          <StyledApp container column maxWidth="none" id="app-container">
            <ConfigProvider configData={configData}>
              <Header eventBus={eventBus.current} />
              {assistant &&
                //TODO: condition to be removed once new assistant is deployed
                configData.shouldUseNewAssistant && (
                  <Assistant
                    assistant={assistant}
                    eventBus={eventBus.current}
                  />
                )}
              <Switch>
                <Route
                  path="/systemcheck"
                  component={() => (
                    <RouteContent>
                      <SystemCheckWrapper />
                    </RouteContent>
                  )}
                />
                <Route
                  path="/authentication-error"
                  component={() => (
                    <RouteContent>
                      <AuthError />
                    </RouteContent>
                  )}
                />
                {configData && configData.routes ? (
                  configData.routes
                    .sort((a, b) =>
                      a.order > b.order ? 1 : b.order > a.order ? -1 : 0
                    )
                    .map((routeConfig) => (
                      <Route
                        key={routeConfig.route}
                        exact={routeConfig.exact}
                        path={routeConfig.route}
                        render={(routeProps) => (
                          <MfeRoute
                            {...routeProps}
                            mfeId={routeConfig.mfeId}
                            requireLogin={routeConfig.requireLogin}
                            productName={configData?.productInfo?.productName}
                            eventBus={eventBus.current}
                          />
                        )}
                      />
                    ))
                ) : (
                  <RouteContent cell>
                    <SystemError />
                  </RouteContent>
                )}
                <Route
                  component={() => (
                    <RouteContent cell>
                      <NotFound />
                    </RouteContent>
                  )}
                />
              </Switch>
              <CallToActionWrapper eventBus={eventBus.current} />
              <AppFooter
                onManageCookies={hasOsano() ? showOsanoDrawer : null}
              />
              <ToolbarFooter
                productCode={productCode}
                feedbackUrl={feedbackUrl}
                eventBus={eventBus.current}
              />
              <EventBusJailService eventBus={eventBus.current} />
              <GainsightService gainsightUserData={gainsightUserData} />
            </ConfigProvider>
          </StyledApp>
        </Theme>
      </StoreProvider>
    </ErrorBoundary>
  );
};

App.displayName = "App";
App.propTypes = {
  configData: PropTypes.object
};
export default App;
